// --- START OF FILE frontend/src/api/auth.ts ---
// REVISED: Improved generic error messages and standardized on ApiError.

import { createApiClient, isAxiosError } from './client';
import { ApiError } from '@/utils/appError';
export { ApiError } from '@/utils/appError';

// Keep your existing API_BASE_URL logic
import { API_BASE_URL } from '@/config/apiConfig';
// const API_BASE_URL = 'http://localhost:8000/api/v1';
console.log("Using API Base URL:", API_BASE_URL);

// --- Interfaces ---
export interface ApiUser {
  id: number;
  username: string;
  phone_number: string | null;  // Null for Google-only users
  email: string | null;
  google_id?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  forum_chat_enabled?: boolean;
  venmo_username?: string | null;
}

export interface GoogleAuthNeedsUsername {
  needs_username: true;
  suggested_username: string;
  email: string | null;
}

export type GoogleAuthResponse = LoginResponse | GoogleAuthNeedsUsername;

export interface AppleAuthNeedsUsername {
  needs_username: true;
  email: string | null;
}

export type AppleAuthResponse = LoginResponse | AppleAuthNeedsUsername;
export interface LoginResponse {
  access_token: string;
  token_type: string;
  is_new_user?: boolean | null;
  username?: string | null;
}
const authApiClient = createApiClient({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json', },
});

export const loginUser = async (phone: string, code: string): Promise<LoginResponse> => {
    try {
        console.log(`Attempting phone login via ${API_BASE_URL}/auth/login`);
        const response = await authApiClient.post<LoginResponse>('/auth/login', { phone_number: phone, code: code, });
        console.log("Login successful, received token.");
        return response.data;
    } catch (error: any) {
        // --- REVISED ERROR MESSAGES ---
        let errorMessage = 'Login failed. Please try again later.'; // More generic default
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error during login:", { message: error.message, url: error.config?.url, status, data: error.response?.data, });
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') {
                // Use backend error directly if it's informative
                errorMessage = backendError;
            } else if (status === 401) {
                // Keep specific 401 message
                errorMessage = "Incorrect phone number or code. Please check and try again.";
            } else if (status === 400 && backendError === 'Inactive user') {
                errorMessage = "This account is currently inactive."; // Specific message for inactive
            } else if (status === 422) {
                errorMessage = "Invalid input format provided.";
            } // Other statuses will use the generic default
        } else {
            console.error("Unexpected error during login:", error);
            // Keep generic default for non-Axios errors
        }
        throw new ApiError(errorMessage, status, { cause: error });
        // --- END REVISED ---
    }
};

export const fetchCurrentUser = async (token: string): Promise<UserWithLeagueContext> => { // <<< CHANGE RETURN TYPE
    if (!token) {
        throw new ApiError("Authentication token missing.", 401);
    }
    try {
        console.log(`Fetching current user via ${API_BASE_URL}/auth/me`);
        // Ensure the .get<TYPE> matches the new expected return type
        const response = await authApiClient.get<UserWithLeagueContext>('/auth/me', { // <<< CHANGE GENERIC TYPE
            headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Successfully fetched user context. Leagues:", response.data.all_my_season_long_leagues?.length);
        return response.data;
    } catch (error: any) {
        let errorMessage = 'Could not verify session. Please log in again.';
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching /auth/me:", { /* ... */ });
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') {
                errorMessage = backendError;
            } else if (status === 401 || status === 403) {
                errorMessage = "Session expired or invalid. Please log in again.";
            }
        } else {
            console.error("Unexpected error fetching /auth/me:", error);
        }
        throw new ApiError(errorMessage, status, { cause: error });
    }
};

// ADD this function
export const requestOtp = async (phone: string): Promise<{ message: string }> => {
    const url = `${API_BASE_URL}/auth/request-otp`;
    console.log(`Requesting OTP via ${url}`);
    try {
      // Ensure phone is passed nested under 'phone_number' key in the body
      const response = await authApiClient.post<{ message: string }>(url, { phone_number: phone });
      console.log("OTP Request successful:", response.data.message);
      return response.data;
    } catch (error: any) {
      let errorMessage = 'Failed to request OTP code.';
      let status: number | undefined;
      if (isAxiosError(error)) {
        status = error.response?.status;
        console.error("Axios error requesting OTP:", { message: error.message, url: error.config?.url, status, data: error.response?.data });
        const backendError = error.response?.data?.detail;
        if (typeof backendError === 'string') {
          errorMessage = backendError;
        } else if (status === 400) {
          errorMessage = "Invalid phone number format provided.";
        } else if (status === 500) {
           errorMessage = "Could not send verification code. Please try again later.";
        }
      } else {
        console.error("Unexpected error requesting OTP:", error);
      }
      throw new ApiError(errorMessage, status, { cause: error });
    }
  };
  
// ADD this function (replaces old loginUser logic)
export const verifyOtp = async (
  phone: string,
  code: string,
  referralSource?: string | null,
): Promise<LoginResponse> => {
      const url = `${API_BASE_URL}/auth/verify-otp`;
      // OTPs are credentials; never write the submitted value to the console.
      console.log(`Verifying OTP via ${url}`);
      try {
          // Ensure data is nested correctly
          const response = await authApiClient.post<LoginResponse>(url, {
            phone_number: phone,
            code,
            ...(referralSource ? { referral_source: referralSource } : {}),
          });
          console.log("OTP Verification successful, received token.");
          return response.data; // Contains access_token and token_type
      } catch (error: any) {
          let errorMessage = 'Login failed. Please try again later.'; // Generic default
          let status: number | undefined;
          if (isAxiosError(error)) {
              status = error.response?.status;
              console.error("Axios error verifying OTP:", { message: error.message, url: error.config?.url, status, data: error.response?.data });
              const backendError = error.response?.data?.detail;
              if (typeof backendError === 'string') {
                  errorMessage = backendError; // Use backend message if available
              } else if (status === 401) {
                  errorMessage = "Incorrect phone number or code."; // Specific 401 message
              } else if (status === 400 && backendError === 'Inactive user') {
                  errorMessage = "This account is currently inactive.";
              } else if (status === 400) {
                  errorMessage = "Invalid phone or code format."; // Generic 400
              } else if (status === 422) {
                  errorMessage = "Invalid input provided.";
              } else if (status === 500) {
                  errorMessage = "Verification service error. Please try again later.";
              }
          } else {
              console.error("Unexpected error verifying OTP:", error);
          }
          throw new ApiError(errorMessage, status, { cause: error });
      }
  };
  
export interface UserLeagueSummary {
  league_id: number;
  league_name: string;
  // Mock leagues stay IN this list (the draft room resolves "my team" from
  // it); nav/list surfaces filter them out at display time (MOCK-DRAFTS.md).
  is_mock?: boolean | null;
  team_id?: number | null; // Optional because user might own a league but not have a team yet
  team_name?: string | null;
  // Dashboard card enrichment: phase pill + draft countdown + manager count
  draft_status?: string | null;
  draft_scheduled_at?: string | null;
  current_fantasy_week?: number | null;
  teams_count?: number | null;
  max_teams?: number | null;
  // In-season only: user's standing in this league
  team_rank?: number | null;
  team_wins?: number | null;
  team_losses?: number | null;
  team_ties?: number | null;
  // Combined leagues only: member competition short names, anchor first
  // (e.g. ["EPL", "La Liga"]). Null/absent = ordinary single-comp league.
  competition_short_names?: string[] | null;
}

export interface UserWithLeagueContext extends ApiUser { // Extends your existing ApiUser
  all_my_season_long_leagues: UserLeagueSummary[];
}

export interface UserProfileUpdatePayload {
  username?: string;
  email?: string;
  venmo_username?: string;
}

export const updateProfile = async (data: UserProfileUpdatePayload, token: string): Promise<ApiUser> => {
  try {
    const response = await authApiClient.patch<ApiUser>('/auth/me', data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to update profile.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendError = error.response?.data?.detail;
      // 400s from explicit HTTPException return a string; 422s from Pydantic
      // validation return an array of {loc, msg, type}. Surface either.
      if (typeof backendError === 'string') {
        errorMessage = backendError;
      } else if (Array.isArray(backendError) && backendError.length > 0) {
        const first = backendError[0];
        const field = Array.isArray(first?.loc)
          ? first.loc.filter((p: any) => p !== 'body').join('.')
          : '';
        const msg = typeof first?.msg === 'string' ? first.msg : 'Validation failed.';
        errorMessage = field ? `${field}: ${msg}` : msg;
      }
    }
    throw new ApiError(errorMessage, status, { cause: error });
  }
};

export const deleteAccount = async (token: string): Promise<{ message: string }> => {
  try {
    const response = await authApiClient.delete<{ message: string }>('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to delete account.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendError = error.response?.data?.detail;
      if (typeof backendError === 'string') {
        errorMessage = backendError;
      }
    }
    throw new ApiError(errorMessage, status, { cause: error });
  }
};

export const googleAuth = async (idToken: string, username?: string): Promise<GoogleAuthResponse> => {
  const url = `${API_BASE_URL}/auth/google`;
  try {
    const body: Record<string, string> = { id_token: idToken };
    if (username) body.username = username;
    const response = await authApiClient.post<GoogleAuthResponse>(url, body);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Google sign-in failed. Please try again.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendError = error.response?.data?.detail;
      if (typeof backendError === 'string') {
        errorMessage = backendError;
      } else if (status === 401) {
        errorMessage = 'Google sign-in failed. Please try again.';
      }
    }
    throw new ApiError(errorMessage, status, { cause: error });
  }
};

export const appleAuth = async (
  idToken: string,
  code?: string,
  username?: string,
): Promise<AppleAuthResponse> => {
  const url = `${API_BASE_URL}/auth/apple`;
  try {
    const body: Record<string, string> = { id_token: idToken };
    if (code) body.code = code;
    if (username) body.username = username;
    const response = await authApiClient.post<AppleAuthResponse>(url, body);
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Apple sign-in failed. Please try again.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendError = error.response?.data?.detail;
      if (typeof backendError === 'string') {
        errorMessage = backendError;
      } else if (status === 401) {
        errorMessage = 'Apple sign-in failed. Please try again.';
      }
    }
    throw new ApiError(errorMessage, status, { cause: error });
  }
};

  // --- You can keep or remove the old loginUser function below ---
  /*
  export const loginUser = async (phone: string, code: string): Promise<LoginResponse> => {
      // ... (old implementation using /auth/login with hardcoded code) ...
      // THIS IS NOW DEPRECATED if using OTP
      console.warn("Using deprecated loginUser function!");
      throw new Error("Deprecated login function called.");
  };
  */
  
// --- END OF FILE frontend/src/api/auth.ts ---
